import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Avatar, Chip, Stack,
  CircularProgress, Alert, Divider, Paper, FormControlLabel, Switch,
  Select, MenuItem, InputAdornment, IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Verified as VerifiedIcon,
  PendingActions as PendingIcon,
  CancelOutlined as RejectedIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordForm from '../ChangePasswordForm';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

function formatAddress(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  return [raw.street, raw.city, raw.state, raw.zip].filter(Boolean).join(', ');
}

// Available service options (kept inline; matches options used in onboarding)
const SERVICE_OPTIONS = [
  'standard_cleaning', 'deep_cleaning', 'move_in_out', 'post_construction',
  'vacation_rental', 'commercial', 'laundry', 'window_cleaning',
];

const COMMUNICATION_METHODS = ['sms', 'email', 'phone'];
const WORKING_PREFERENCES = [
  { value: 'alone', label: 'Alone' },
  { value: 'with_partner', label: 'With a partner' },
  { value: 'no_preference', label: 'No preference' },
];

const ONBOARDING_STATUS = {
  approved:        { label: 'Approved',        color: 'success', icon: <VerifiedIcon fontSize="small" /> },
  pending_review:  { label: 'Pending review',  color: 'warning', icon: <PendingIcon fontSize="small" /> },
  incomplete:      { label: 'Incomplete',      color: 'default', icon: <PendingIcon fontSize="small" /> },
  rejected:        { label: 'Rejected',        color: 'error',   icon: <RejectedIcon fontSize="small" /> },
};

/**
 * EditableSection — small wrapper that toggles between read view and edit view.
 *
 * Each section provides:
 *   - title
 *   - readView: JSX rendered when not editing
 *   - editView: JSX rendered when editing (forms)
 *   - onSave: () => Promise<void>; called when user taps Save
 *
 * Keeps Save/Cancel + spinner state inside the section so the rest of the
 * panel doesn't have to thread state through every field.
 */
const EditableSection = ({ title, children, readView, editView, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await onSave();
      setSuccess('Saved.');
      setEditing(false);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
          {title}
        </Typography>
        {!editing && onSave && (
          <Button size="small" startIcon={<EditIcon />} onClick={() => { setEditing(true); setSuccess(''); }}>
            Edit
          </Button>
        )}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {editing ? editView : (readView || children)}

      {editing && (
        <Box display="flex" gap={1} mt={2} justifyContent="flex-end">
          <Button size="small" onClick={() => { setEditing(false); setError(''); }} disabled={saving} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

/**
 * Read-only key/value pair, used inside the read view of each section.
 */
const Field = ({ label, value, fallback = '—' }) => (
  <Box mb={1}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || fallback}</Typography>
  </Box>
);

const MaidProfilePanel = () => {
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Section-local edit state — controlled inputs reset to profile values on cancel/save
  const [personal, setPersonal] = useState({ firstName: '', lastName: '', phoneNumber: '', residentialAddress: '' });
  const [work, setWork]         = useState({ hourlyRate: '', maxTravelRadius: '', services: [], hasVehicle: false, bringsSupplies: false, workingPreference: 'no_preference' });
  const [schedule, setSchedule] = useState({ hoursPerWeek: '', weekendAvailable: false, emergencyAvailable: false });
  const [prefs, setPrefs]       = useState({ comfortableWithPets: false, willingToWorkInTeams: false, preferredCommunicationMethod: 'sms' });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API_URL}/api/maids/me`, { headers: authHeaders() });
      const p = res.data;
      setProfile(p);

      // Hydrate edit-state buffers from server data
      setPersonal({
        firstName: p.user?.firstName || '',
        lastName: p.user?.lastName || '',
        phoneNumber: p.user?.phoneNumber || '',
        residentialAddress: formatAddress(p.personalInfo?.residentialAddress),
      });
      setWork({
        hourlyRate: p.hourlyRate ?? '',
        maxTravelRadius: p.maxTravelRadius ?? '',
        services: Array.isArray(p.services) ? p.services : [],
        hasVehicle: !!p.hasVehicle,
        bringsSupplies: !!p.bringsSupplies,
        workingPreference: p.workingPreference || 'no_preference',
      });
      setSchedule({
        hoursPerWeek: p.schedulePreferences?.hoursPerWeek ?? '',
        weekendAvailable: !!p.schedulePreferences?.weekendAvailable,
        emergencyAvailable: !!p.schedulePreferences?.emergencyAvailable,
      });
      setPrefs({
        comfortableWithPets: !!p.preferences?.comfortableWithPets,
        willingToWorkInTeams: !!p.preferences?.willingToWorkInTeams,
        preferredCommunicationMethod: p.preferences?.preferredCommunicationMethod || 'sms',
      });
    } catch (err) {
      logger.log('Failed to load maid profile:', err);
      // 404 just means no profile row yet — render the read-only header anyway
      if (err?.response?.status !== 404) {
        setLoadError('Could not load your profile.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchProfile();
  }, [user, fetchProfile]);

  // ─── save handlers (one per section, calls PATCH /me with that section's body) ───
  const patchMe = async (body) => {
    await axios.patch(`${API_URL}/api/maids/me`, body, { headers: authHeaders() });
    await fetchProfile();
  };

  const savePersonal = () => patchMe(personal);
  const saveWork = () => patchMe({
    hourlyRate: work.hourlyRate === '' ? undefined : Number(work.hourlyRate),
    maxTravelRadius: work.maxTravelRadius === '' ? undefined : Number(work.maxTravelRadius),
    services: work.services,
    hasVehicle: work.hasVehicle,
    bringsSupplies: work.bringsSupplies,
    workingPreference: work.workingPreference,
  });
  const saveSchedule = () => patchMe({
    schedulePreferences: {
      hoursPerWeek: schedule.hoursPerWeek === '' ? null : Number(schedule.hoursPerWeek),
      weekendAvailable: schedule.weekendAvailable,
      emergencyAvailable: schedule.emergencyAvailable,
    },
  });
  const savePrefs = () => patchMe({
    preferences: {
      comfortableWithPets: prefs.comfortableWithPets,
      willingToWorkInTeams: prefs.willingToWorkInTeams,
      preferredCommunicationMethod: prefs.preferredCommunicationMethod,
    },
  });

  // ─── render ───
  const initials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() : '?';

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  const status = profile?.onboardingStatus
    ? (ONBOARDING_STATUS[profile.onboardingStatus] || ONBOARDING_STATUS.incomplete)
    : null;

  const toggleService = (s) =>
    setWork(w => ({ ...w, services: w.services.includes(s) ? w.services.filter(x => x !== s) : [...w.services, s] }));

  return (
    <Box>
      {/* Header */}
      <Box display="flex" flexDirection="column" alignItems="center" py={3} gap={1}>
        <Avatar sx={{ width: 72, height: 72, fontSize: 28, bgcolor: 'primary.main' }}>
          {initials}
        </Avatar>
        <Typography variant="h6" fontWeight={700}>
          {profile?.user?.firstName || user?.firstName} {profile?.user?.lastName || user?.lastName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {profile?.user?.email || user?.email}
        </Typography>
        {status && (
          <Chip icon={status.icon} label={status.label} color={status.color} size="small" sx={{ mt: 0.5 }} />
        )}
        {profile?.rating != null && profile.totalReviews > 0 && (
          <Typography variant="caption" color="text.secondary">
            ★ {Number(profile.rating).toFixed(1)} ({profile.totalReviews} review{profile.totalReviews === 1 ? '' : 's'})
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {/* ───────── Personal ───────── */}
      <EditableSection
        title="Personal"
        onSave={savePersonal}
        readView={
          <>
            <Field label="Full name" value={[personal.firstName, personal.lastName].filter(Boolean).join(' ')} />
            <Field label="Phone" value={personal.phoneNumber} />
            <Field label="Home address" value={personal.residentialAddress} />
          </>
        }
        editView={
          <Stack spacing={1.5}>
            <Box display="flex" gap={1}>
              <TextField size="small" label="First name" value={personal.firstName}
                onChange={e => setPersonal(p => ({ ...p, firstName: e.target.value }))} fullWidth />
              <TextField size="small" label="Last name" value={personal.lastName}
                onChange={e => setPersonal(p => ({ ...p, lastName: e.target.value }))} fullWidth />
            </Box>
            <TextField size="small" label="Phone" value={personal.phoneNumber}
              onChange={e => setPersonal(p => ({ ...p, phoneNumber: e.target.value }))}
              inputProps={{ inputMode: 'tel' }} fullWidth />
            <TextField size="small" label="Home address" value={personal.residentialAddress}
              onChange={e => setPersonal(p => ({ ...p, residentialAddress: e.target.value }))}
              placeholder="Street, City, State ZIP" multiline minRows={2} fullWidth
              helperText="Used to compute distance to available jobs." />
          </Stack>
        }
      />

      {/* ───────── Work preferences ───────── */}
      <EditableSection
        title="Work preferences"
        onSave={saveWork}
        readView={
          <>
            <Field label="Hourly rate" value={work.hourlyRate ? `$${work.hourlyRate}/hr` : null} />
            <Field label="Max travel" value={work.maxTravelRadius ? `${work.maxTravelRadius} mi` : null} />
            <Field label="Has vehicle" value={work.hasVehicle ? 'Yes' : 'No'} />
            <Field label="Brings own supplies" value={work.bringsSupplies ? 'Yes' : 'No'} />
            <Field label="Working preference" value={WORKING_PREFERENCES.find(o => o.value === work.workingPreference)?.label} />
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">Services offered</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {work.services.length === 0
                  ? <Typography variant="body2">—</Typography>
                  : work.services.map(s => <Chip key={s} size="small" label={s.replace(/_/g, ' ')} />)}
              </Box>
            </Box>
          </>
        }
        editView={
          <Stack spacing={1.5}>
            <Box display="flex" gap={1}>
              <TextField size="small" label="Hourly rate" type="number" value={work.hourlyRate}
                onChange={e => setWork(w => ({ ...w, hourlyRate: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} fullWidth />
              <TextField size="small" label="Max travel" type="number" value={work.maxTravelRadius}
                onChange={e => setWork(w => ({ ...w, maxTravelRadius: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }} fullWidth />
            </Box>
            <FormControlLabel control={<Switch checked={work.hasVehicle} onChange={e => setWork(w => ({ ...w, hasVehicle: e.target.checked }))} />} label="I have a vehicle" />
            <FormControlLabel control={<Switch checked={work.bringsSupplies} onChange={e => setWork(w => ({ ...w, bringsSupplies: e.target.checked }))} />} label="I bring my own supplies" />
            <Select size="small" value={work.workingPreference}
              onChange={e => setWork(w => ({ ...w, workingPreference: e.target.value }))} fullWidth>
              {WORKING_PREFERENCES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>Services I offer</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {SERVICE_OPTIONS.map(s => (
                  <Chip key={s} label={s.replace(/_/g, ' ')} size="small"
                    color={work.services.includes(s) ? 'primary' : 'default'}
                    variant={work.services.includes(s) ? 'filled' : 'outlined'}
                    onClick={() => toggleService(s)} clickable />
                ))}
              </Box>
            </Box>
          </Stack>
        }
      />

      {/* ───────── Availability ───────── */}
      <EditableSection
        title="Availability"
        onSave={saveSchedule}
        readView={
          <>
            <Field label="Hours per week" value={schedule.hoursPerWeek ? `${schedule.hoursPerWeek} hrs` : null} />
            <Field label="Available weekends" value={schedule.weekendAvailable ? 'Yes' : 'No'} />
            <Field label="Available for emergencies" value={schedule.emergencyAvailable ? 'Yes' : 'No'} />
          </>
        }
        editView={
          <Stack spacing={1.5}>
            <TextField size="small" label="Hours per week" type="number" value={schedule.hoursPerWeek}
              onChange={e => setSchedule(s => ({ ...s, hoursPerWeek: e.target.value }))} fullWidth />
            <FormControlLabel control={<Switch checked={schedule.weekendAvailable} onChange={e => setSchedule(s => ({ ...s, weekendAvailable: e.target.checked }))} />} label="Available on weekends" />
            <FormControlLabel control={<Switch checked={schedule.emergencyAvailable} onChange={e => setSchedule(s => ({ ...s, emergencyAvailable: e.target.checked }))} />} label="Available for emergency / same-day jobs" />
          </Stack>
        }
      />

      {/* ───────── Preferences ───────── */}
      <EditableSection
        title="Preferences"
        onSave={savePrefs}
        readView={
          <>
            <Field label="Comfortable with pets" value={prefs.comfortableWithPets ? 'Yes' : 'No'} />
            <Field label="Willing to work in teams" value={prefs.willingToWorkInTeams ? 'Yes' : 'No'} />
            <Field label="Preferred communication" value={prefs.preferredCommunicationMethod?.toUpperCase()} />
          </>
        }
        editView={
          <Stack spacing={1.5}>
            <FormControlLabel control={<Switch checked={prefs.comfortableWithPets} onChange={e => setPrefs(p => ({ ...p, comfortableWithPets: e.target.checked }))} />} label="Comfortable working around pets" />
            <FormControlLabel control={<Switch checked={prefs.willingToWorkInTeams} onChange={e => setPrefs(p => ({ ...p, willingToWorkInTeams: e.target.checked }))} />} label="Willing to work in teams" />
            <Select size="small" value={prefs.preferredCommunicationMethod}
              onChange={e => setPrefs(p => ({ ...p, preferredCommunicationMethod: e.target.value }))} fullWidth>
              {COMMUNICATION_METHODS.map(m => <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>)}
            </Select>
          </Stack>
        }
      />

      {/* ───────── Password ───────── */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1.5 }}>
          Password
        </Typography>
        <ChangePasswordForm />
      </Paper>

      {/* ───────── Status (read-only) ───────── */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Account status
        </Typography>
        <Box mt={1.5}>
          <Field label="Onboarding" value={status?.label} />
          <Field label="Email" value={profile?.user?.email || user?.email} />
          <Typography variant="caption" color="text.secondary">
            To change your email, documents, or payment method, contact an administrator.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default MaidProfilePanel;
